import { useMergedRef } from '@mantine/hooks';
import cx from 'clsx';
import { useAnimate } from 'motion/react';
import {
  Children,
  type CSSProperties,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useEffect,
  useEffectEvent,
} from 'react';

import classes from './Bezel.module.css';
import { CORNER_ORDER, type CornerQuad, resolveQuad } from './geometry.ts';
import { useBezelGeometry } from './useBezelGeometry.ts';

/** Build a style object from custom-property entries, dropping undefined so absent
 *  overrides fall through to the inherited/registered CSS value rather than clobbering it. */
function definedVars(vars: Record<string, string | number | undefined>): CSSProperties {
  return Object.fromEntries(Object.entries(vars).filter(([, value]) => value !== undefined)) as CSSProperties;
}

/** Accept a bare number as px, or pass a CSS length string through untouched. */
function cssLength(value: string | number | undefined): string | undefined {
  return typeof value === 'number' ? `${value}px` : value;
}

/** Serialise a corner quad to a CSS `<length>+` value: `12` → "12px", `[12,0,12,0]` → "12px 0px 12px 0px". */
function cssQuad(value: CornerQuad | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  return typeof value === 'number' ? `${value}px` : value.map((n) => `${n}px`).join(' ');
}

// Effect layers. Each is position:absolute and reads geometry (clip paths) from the
// inherited --bezel-*-path vars the root sets — no React context. Layers stack
// freely; group them in <Bezel.Effects> so the root splits them from content.

/** Color overrides accept any CSS color (incl. var()); appearance derives via relative color. */
type ColorLayerProps = {
  /** Any CSS color, e.g. `var(--mantine-color-cyan-4)`. */
  color?: string;
  /** Strength multiplier, 0..1. `0` opts the layer out. */
  intensity?: number;
};

function BezelBackground({ color, intensity }: ColorLayerProps) {
  return (
    <div
      className={cx(classes.effect, classes.background)}
      style={definedVars({ '--bezel-background-color': color, '--bezel-background-intensity': intensity })}
    />
  );
}

function BezelGlow({ color, intensity, blur }: ColorLayerProps & { blur?: string | number }) {
  return (
    <div
      className={cx(classes.effect, classes.glow)}
      style={definedVars({
        '--bezel-glow-color': color,
        '--bezel-glow-intensity': intensity,
        '--bezel-glow-blur': cssLength(blur),
      })}
    />
  );
}

/** Rotating conic glow around the frame (hud's idle border-animation as a standalone
 *  band): a masked, blurred ring whose sweep + opacity-breathing are knob-driven. */
function BezelOuterGlow({
  color,
  intensity,
  blur,
  speed,
  pulseIntensity,
}: ColorLayerProps & { blur?: string | number; speed?: string; pulseIntensity?: number }) {
  return (
    <div
      className={cx(classes.effect, classes.outerGlow)}
      style={definedVars({
        '--bezel-outer-glow-color': color,
        '--bezel-outer-glow-intensity': intensity,
        '--bezel-outer-glow-blur': cssLength(blur),
        '--bezel-outer-glow-speed': speed,
        '--bezel-outer-glow-pulse-intensity': pulseIntensity,
      })}
    />
  );
}

export type BezelStream = 'idle' | 'thinking' | 'speaking' | 'off';

function BezelBorder({
  color,
  intensity,
  stream = 'off',
  speed,
  blur,
}: ColorLayerProps & { stream?: BezelStream; speed?: string; blur?: string | number }) {
  return (
    <>
      <div
        className={cx(classes.effect, classes.border)}
        style={definedVars({ '--bezel-border-color': color, '--bezel-border-intensity': intensity })}
      />
      {stream !== 'off' && (
        <>
          <div className={cx(classes.effect, classes.tracer)} style={definedVars({ '--bezel-border-color': color })} />
          <div
            className={cx(classes.effect, classes.stream)}
            data-stream={stream}
            style={definedVars({
              '--bezel-border-color': color,
              '--bezel-stream-speed': speed,
              '--bezel-stream-blur': cssLength(blur),
            })}
          />
        </>
      )}
    </>
  );
}

function BezelCorners({ color, opacity = 1, intensity }: ColorLayerProps & { opacity?: CornerQuad }) {
  const perCorner = resolveQuad(opacity);
  return CORNER_ORDER.map((corner, index) => (
    <div
      className={cx(classes.effect, classes.corner)}
      data-corner={corner}
      key={corner}
      style={definedVars({
        '--bezel-corner-color': color,
        '--bezel-corner-opacity': perCorner[index],
        '--bezel-corner-intensity': intensity,
        clipPath: `var(--bezel-corner-${corner}-path)`,
      })}
    />
  ));
}

function BezelGlint({ color }: { color?: string }) {
  return <div className={cx(classes.effect, classes.glint)} style={definedVars({ '--bezel-glint-color': color })} />;
}

export type BezelProps = {
  /** Effect layers (<Bezel.Glow> …) and/or content; effects omitted → theme default set. */
  children?: ReactNode;
  /** Chamfer size per corner (px). Geometry resolves once on the root. */
  bevel?: CornerQuad;
  /** Arc radius per corner (px); wins over bevel where > 0. */
  radius?: CornerQuad;
  strokeWidth?: number;
  cornerThickness?: number;
  cornerLength?: number;
  /** Outer-glow band geometry as a "<width> <spread>" length pair, e.g. "10px 4px"
   *  (band thickness, inset from the edge). Consumed by <Bezel.OuterGlow>. */
  outerGlowBand?: string;
  /** Any CSS color (incl. var()); the whole bezel derives from it via relative color. */
  primaryColor?: string;
  /** Any CSS color; defaults to primary unless an app theme rebinds it. */
  accentColor?: string;
  /** Ambient liveness, 0..1 — faster/brighter pulse, tracer and glow as it rises. */
  activity?: number;
  /** Entrance reveal animation. */
  animate?: boolean;
  className?: string;
  style?: CSSProperties;
};

/**
 * Grouping slot for effect layers. The root reads its `children` as the effect
 * stack and renders everything else as content — no per-layer markers. Never
 * mounted directly; the root extracts its children.
 */
function BezelEffects({ children }: { children?: ReactNode }): ReactNode {
  return children;
}

function isBezelEffects(node: ReactNode): node is ReactElement<{ children?: ReactNode }> {
  return isValidElement(node) && node.type === BezelEffects;
}

const DEFAULT_EFFECTS: ReactElement<{ children?: ReactNode }> = (
  <BezelEffects>
    <BezelBackground />
    <BezelGlow />
    <BezelBorder />
    <BezelCorners />
    <BezelGlint />
  </BezelEffects>
);

/** Themed container with beveled/rounded corners and composable, stackable effect layers. */
export const Bezel = Object.assign(
  function BezelRoot({
    children,
    bevel,
    radius,
    strokeWidth,
    cornerThickness,
    cornerLength,
    outerGlowBand,
    primaryColor,
    accentColor,
    activity,
    animate: shouldAnimate = true,
    className,
    style,
  }: BezelProps) {
    const { ref, width, height, paths } = useBezelGeometry({
      bevel,
      radius,
      strokeWidth,
      cornerThickness,
      cornerLength,
      outerGlowBand,
    });
    const isReady = width > 0 && height > 0;

    // Effect layers live in a single <Bezel.Effects> slot (default set when
    // omitted); every other child is content.
    const childArray = Children.toArray(children);
    const effectsSlot = childArray.find(isBezelEffects) ?? DEFAULT_EFFECTS;
    const content = childArray.filter((child) => child !== effectsSlot);
    // toArray keys the layers and drops conditional-false slots.
    const effects = Children.toArray(effectsSlot.props.children);

    const [scope, animate] = useAnimate();
    const animateEvent = useEffectEvent(animate);
    const mergedRef = useMergedRef(ref, scope);

    useEffect(() => {
      if (!shouldAnimate) {
        return;
      }
      animateEvent(scope.current, { '--bezel-reveal': [0, 1] }, { duration: 0.75, ease: 'easeOut' });
    }, [scope, shouldAnimate]);

    return (
      <div
        className={cx(classes.container, className)}
        ref={mergedRef}
        style={definedVars({
          '--bezel-primary': primaryColor,
          '--bezel-accent': accentColor,
          '--bezel-activity': activity,
          '--bezel-bevel': cssQuad(bevel),
          '--bezel-radius': cssQuad(radius),
          '--bezel-stroke-width': cssLength(strokeWidth),
          '--bezel-corner-thickness': cssLength(cornerThickness),
          '--bezel-corner-length': cssLength(cornerLength),
          '--bezel-outer-glow-band': outerGlowBand,
          '--bezel-clip-path': paths?.clip,
          '--bezel-stroke-path': paths?.stroke,
          '--bezel-corner-tl-path': paths?.corners.tl,
          '--bezel-corner-tr-path': paths?.corners.tr,
          '--bezel-corner-br-path': paths?.corners.br,
          '--bezel-corner-bl-path': paths?.corners.bl,
          '--bezel-outer-glow-path': paths?.outerGlow,
          ...style,
        })}
      >
        <div className={classes.content}>{content}</div>
        <div className={classes.effects}>{isReady && effects}</div>
      </div>
    );
  },
  {
    Background: BezelBackground,
    Glow: BezelGlow,
    OuterGlow: BezelOuterGlow,
    Border: BezelBorder,
    Corners: BezelCorners,
    Glint: BezelGlint,
    Effects: BezelEffects,
  },
);
